import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Header } from './shared/components/Header';
import { Footer } from './shared/components/Footer';
import { Home } from './routes/Home';
import { loadFromStorage, saveToStorage } from './shared/utils/localStorage';
import { toolRegistry } from './shared/data/toolRegistry';
import { X } from 'lucide-react';

// Lazy loading tools
const FredQuickChart = React.lazy(() => import('./tools/fred-quickchart/FredQuickChart'));
const CpiCalculator = React.lazy(() => import('./tools/cpi-inflation-calculator/CpiCalculator'));
const ElasticitySolver = React.lazy(() => import('./tools/elasticity-solver/ElasticitySolver'));
const SupplyDemandTool = React.lazy(() => import('./tools/supply-demand-equilibrium/SupplyDemandTool'));
const TaxIncidenceTool = React.lazy(() => import('./tools/tax-subsidy-incidence/TaxIncidenceTool'));
const SurplusTool = React.lazy(() => import('./tools/surplus-deadweight-loss/SurplusTool'));
const GdpCalculator = React.lazy(() => import('./tools/gdp-calculator/GdpCalculator'));
const UnemploymentCalculator = React.lazy(() => import('./tools/unemployment-rate-calculator/UnemploymentCalculator'));
const MoneyMultiplierTool = React.lazy(() => import('./tools/money-multiplier/MoneyMultiplierTool'));
const ExchangeArbitrageTool = React.lazy(() => import('./tools/exchange-arbitrage/ExchangeArbitrageTool'));
const LorenzGiniTool = React.lazy(() => import('./tools/lorenz-gini/LorenzGiniTool'));
const NpvIrrTool = React.lazy(() => import('./tools/npv-irr-sheet/NpvIrrTool'));

// Lazy load compliance pages
const Privacy = React.lazy(() => import('./routes/Privacy'));
const Terms = React.lazy(() => import('./routes/Terms'));
const Disclaimer = React.lazy(() => import('./routes/Disclaimer'));

function Layout({ children, searchQuery, setSearchQuery }: { children: React.ReactNode; searchQuery: string; setSearchQuery: (q: string) => void }) {
  const [darkMode, setDarkMode] = useState(() => loadFromStorage<boolean>('dark_theme', false));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    saveToStorage('dark_theme', darkMode);
  }, [darkMode]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const toggleDarkMode = () => setDarkMode(!darkMode);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const categories = {
    microeconomics: 'Microeconomics',
    macroeconomics: 'Macroeconomics',
    data: 'Data & Policy',
    finance: 'Finance & Decision',
  };

  const sidebarContent = (
    <nav className="space-y-6">
      {Object.entries(categories).map(([catKey, catName]) => {
        const catTools = toolRegistry.filter((t) => t.category === catKey);
        return (
          <div key={catKey} className="space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 pl-3">
              {catName}
            </h4>
            <div className="space-y-1">
              {catTools.map((t) => {
                const isActive = location.pathname === t.route;
                return (
                  <Link
                    key={t.id}
                    to={t.route}
                    className={`block px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
                      isActive
                        ? 'bg-brand-500 text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
                  >
                    {t.shortTitle}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-200">
      <Header
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        toggleMobileMenu={toggleMobileMenu}
      />

      {/* Main Layout Container */}
      <div className="flex-1 flex w-full relative">
        {/* Persistent Desktop Sidebar */}
        <aside className="w-64 border-r border-slate-200/50 dark:border-slate-800/50 bg-white dark:bg-slate-950 p-5 hidden lg:block overflow-y-auto max-h-[calc(100vh-4rem)] sticky top-16">
          {sidebarContent}
        </aside>

        {/* Mobile Menu Backdrop & Drawer */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={toggleMobileMenu} />
            {/* Drawer */}
            <aside className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 p-5 overflow-y-auto shadow-2xl flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-4">
                  <span className="font-display font-bold text-slate-800 dark:text-slate-100">
                    Econ<span className="text-brand-500">Dock</span> Navigation
                  </span>
                  <button onClick={toggleMobileMenu} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {sidebarContent}
              </div>
            </aside>
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-x-hidden flex flex-col min-h-[calc(100vh-4rem)]">
          <div className="flex-1">
            {children}
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <BrowserRouter>
      <Layout searchQuery={searchQuery} setSearchQuery={setSearchQuery}>
        <Suspense
          fallback={
            <div className="flex-1 flex items-center justify-center p-12">
              <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home searchQuery={searchQuery} />} />
            <Route path="/tools/fred-quickchart" element={<FredQuickChart />} />
            <Route path="/tools/cpi-inflation-calculator" element={<CpiCalculator />} />
            <Route path="/tools/elasticity-solver" element={<ElasticitySolver />} />
            <Route path="/tools/supply-demand-equilibrium" element={<SupplyDemandTool />} />
            <Route path="/tools/tax-subsidy-incidence" element={<TaxIncidenceTool />} />
            <Route path="/tools/surplus-deadweight-loss" element={<SurplusTool />} />
            <Route path="/tools/gdp-calculator" element={<GdpCalculator />} />
            <Route path="/tools/unemployment-rate-calculator" element={<UnemploymentCalculator />} />
            <Route path="/tools/money-multiplier" element={<MoneyMultiplierTool />} />
            <Route path="/tools/exchange-arbitrage" element={<ExchangeArbitrageTool />} />
            <Route path="/tools/lorenz-gini" element={<LorenzGiniTool />} />
            <Route path="/tools/npv-irr-sheet" element={<NpvIrrTool />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}
